import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy } from "lucide-react";

const CATEGORIES = ["Support", "Installation", "Sales", "Admin", "Renewal", "Other"];
const PRIORITIES = ["Low", "Medium", "High"];
const empty = { title: "", category: "Support", priority: "Medium", tech_assigned: "", description: "" };

export default function CaseFormDialog({ open, onOpenChange, clientId, onSaved }) {
  const [form, setForm] = useState(empty);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    api.get("/team/members").then((r) => setMembers(r.data)).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Case title is required"); return; }
    setSaving(true);
    try {
      await api.post("/cases", { client_id: clientId, ...form });
      toast.success("Case opened");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error("Could not open case"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><LifeBuoy size={18} className="text-primary" /> Open Case</DialogTitle>
          <DialogDescription>Log a case for this customer and assign it to any team member.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label>Case title *</Label><Input data-testid="case-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-sm" placeholder="e.g. Follow up on renewal / installation help" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Type</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="rounded-sm" data-testid="case-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="rounded-sm" data-testid="case-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Assign to</Label>
            <Select value={form.tech_assigned} onValueChange={(v) => set("tech_assigned", v)}>
              <SelectTrigger className="rounded-sm" data-testid="case-tech"><SelectValue placeholder="Select a team member" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}{m.role ? ` · ${m.role}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><Label>Description</Label><Textarea data-testid="case-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-sm" placeholder="Describe what needs to be done…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm" onClick={submit} disabled={saving} data-testid="case-submit">{saving ? "Opening…" : "Open Case"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
